import React, {
  Dispatch,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { isExpired } from "react-jwt";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Constants, RouteConstants } from "../constants";
import {
  IAuthenticatedUser,
  LogoutReason,
} from "../models/authenticated-user.interface";
import { ITokenUser } from "../models/token.interface";
import authorizationService from "../services/authorization.service";
import {
  getLogoutUrl,
  getUserFromToken,
  isLoginRoute,
} from "../utils/authorizationUtil";
import LocalStorageUtil from "../utils/localStorage";
import logger from "../utils/logger";
import { browserHistory } from "../utils/routes.util";

interface IAuthState {
  user: IAuthenticatedUser | null;
}

export enum AuthActionType {
  LOGOUT = "LOGOUT",
  SET_USER = "SET_USER",
}

interface IAuthReducerAction {
  type: AuthActionType;
  payload?: any;
}

interface IAuthContext {
  state: IAuthState;
  dispatch: Dispatch<IAuthReducerAction>;
}

const authInitState: IAuthState = {
  user: null,
};

const AuthContext = React.createContext<IAuthContext>({
  state: authInitState,
  dispatch: () => {},
});

const authReducer = (
  state: IAuthState,
  action: IAuthReducerAction
): IAuthState => {
  switch (action.type) {
    case AuthActionType.LOGOUT:
      return { ...state, ...authInitState };
    case AuthActionType.SET_USER:
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

const handleMultiTabLogout = (prevUserToken: string) => {
  const prevUser = getUserFromToken(prevUserToken);

  if (!prevUser) {
    // Unable to get copy of the last logged in user -> logout to default page
    browserHistory.push(RouteConstants.LOGIN_PATH);
    return;
  }

  const logoutReason = LocalStorageUtil.get(
    Constants.LOGOUT_REASON_KEY
  ) as LogoutReason;

  const logoutRedirectUrl = getLogoutUrl(logoutReason, {
    isSso: prevUser.IsSso,
    signOutUrl: prevUser.SignOutUrl,
    signInUrl: prevUser.SignInUrl,
  });

  browserHistory.push(logoutRedirectUrl);
};

const onStorageChange = (storageEvent: StorageEvent) => {
  if (storageEvent.key !== Constants.JWT_KEY) return;

  const newToken = storageEvent.newValue;
  if (newToken) return;

  // newToken doesn't exists -> Logout from another window has happened
  logger.info(AuthContextProvider.name, "Detected logout from another window");
  const oldToken = storageEvent.oldValue;
  handleMultiTabLogout(oldToken ?? "");
};

export const AuthContextProvider: React.FC = ({
  children,
}: PropsWithChildren<{}>) => {
  const [state, dispatch] = useReducer(authReducer, authInitState);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    window.addEventListener("storage", onStorageChange);

    return () => {
      window.removeEventListener("storage", onStorageChange);
    };
  }, []);

  useEffect(() => {
    if (
      [
        RouteConstants.VIEW_ECG_PATH,
        RouteConstants.VIEW_PRESCRIPTION_PATH,
      ].includes(location.pathname)
    ) {
      navigate({
        pathname: location.pathname,
        search: getQueryParamsWithoutToken(),
      });
      return;
    }

    const token = searchParams.get("token");

    /**
     * If page is reset-password and loaded with a token in query
     * (e.g. "domain.com/auth/reset-password?token=<...>"")
     * --> Handle Reset password with token
     */
    if (token && location.pathname === RouteConstants.RESET_PASSWORD_PATH) {
      logger.info(
        AuthContextProvider.name,
        "Detected reset password token...",
        token
      );
      navigate(RouteConstants.RESET_PASSWORD_PATH, {
        state: { token },
      });
      return;
    }

    /**
     * If page loaded with a token in query
     * (e.g. "domain.com/?token=<...>"")
     * --> Handle SSO login
     */
    if (token) {
      logger.info(AuthContextProvider.name, "Detected SSO token...", token);

      // Pass SSO handling to our login page component
      navigate(
        { pathname: RouteConstants.LOGIN_PATH },
        {
          state: {
            token,
            from: location.pathname + getQueryParamsWithoutToken(),
          },
        }
      );
      return;
    }

    if (location.pathname === RouteConstants.FORGOT_PASSWORD_PATH) return;

    logger.info(AuthContextProvider.name, "Checking stored token status...");
    const storedToken = LocalStorageUtil.get(Constants.JWT_KEY);
    const tokenUser = getUserFromToken(storedToken);

    if (storedToken && isExpired(storedToken)) {
      logger.error(AuthContextProvider.name, "Token expired.");
      navigateToLogin(tokenUser ?? undefined);
      return;
    }

    if (!storedToken || !tokenUser) {
      logger.error(AuthContextProvider.name, "Token is invalid.");
      navigateToLogin(tokenUser ?? undefined);
      return;
    }

    const authUser = authorizationService.createAuthenticatedUser(tokenUser);

    dispatch({
      type: AuthActionType.SET_USER,
      payload: authUser,
    });

    navigate({ pathname: "/", search: getQueryParamsWithoutToken() });
  }, []); // eslint-disable-line

  function getQueryParamsWithoutToken() {
    /**
     * Token is saved in navigation state when needed, remove from url
     */
    searchParams.delete("token");
    return `?${searchParams.toString()}`;
  }

  function navigateToLogin(tokenUser?: ITokenUser) {
    if (tokenUser && tokenUser.IsSso) {
      logger.info(AuthContextProvider.name, "Redirect to SSO login.");
      browserHistory.push(tokenUser.SignInUrl);
    } else {
      navigate(
        {
          pathname: RouteConstants.LOGIN_PATH,
        },
        {
          state: {
            from: !isLoginRoute(location?.pathname)
              ? `${location.pathname}${getQueryParamsWithoutToken()}`
              : undefined,
          },
        }
      );
    }
  }

  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
